import React, { useEffect, useMemo, useState } from "react";
import {
  getProgress,
  getProgressSummary,
  getUpdateRunList,
} from "../../api/process";
import { buildWbsTree } from "../../utils/buildWbsTree";
import TreeTable from "../../components/process/TreeTable";
import SummaryCard from "../../components/process/SummaryCard";
import PageHeader from "../../components/common/ui/PageHeader";
import { formatDate } from "../../utils/date";


const Progress = () => {
  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [project, setProject] = useState({ name: "", summary: null });
  const [search, setSearch] = useState("");

  const [actualRuns, setActualRuns] = useState([]);
  const [baselineRuns, setBaselineRuns] = useState([]);
  const [selectedActual, setSelectedActual] = useState(null);
  const [selectedBaseline, setSelectedBaseline] = useState(null);

  /** ───────────────────────────────────────────
   *  📌 Run 목록 불러오기
   *  ─────────────────────────────────────────── */
  useEffect(() => {
    const loadRuns = async () => {
      try {
        const runs = await getUpdateRunList();

        const actual = runs.filter((r) => r.run_type === "A");
        const baseline = runs.filter((r) => r.run_type === "B");

        setActualRuns(actual);
        setBaselineRuns(baseline);

        setSelectedActual(actual.at(0)?.id || null);
        setSelectedBaseline(baseline.at(0)?.id || null);
      } catch (err) {
        console.error("Run load failed:", err);
      }
    };

    loadRuns();
  }, []);

  /** ───────────────────────────────────────────
   *  📌 데이터 로드 (선택 변경)
   *  ─────────────────────────────────────────── */
  useEffect(() => {
    if (!selectedActual) return;

    const fetchProgress = async () => {
      try {
        setLoading(true);

        const [data, summary] = await Promise.all([
          getProgress(selectedActual, selectedBaseline),
          getProgressSummary(selectedActual, selectedBaseline),
        ]);

        // ✅ 이거 반드시 필요
        const tree = buildWbsTree(data.activities);
        setTreeData(tree);

        setProject({
          ...data.project,
          ...summary?.project,
          summary: summary?.project?.summary || null,
        });
      } catch (err) {
        console.error("Progress load error:", err);
      } finally {
        setLoading(false);
      }
    };


    fetchProgress();
  }, [selectedActual, selectedBaseline]);

  /** ───────────────────────────────────────────
   *  📌 검색 필터
   *  ─────────────────────────────────────────── */
  const filteredTree = useMemo(() => {
    if (!search) return treeData;
    const lower = search.toLowerCase();

    const filterNode = (node) => {
      const acts = (node.activities || []).filter(
        (a) =>
          a.name.toLowerCase().includes(lower) ||
          a.activity_code.toLowerCase().includes(lower)
      );

      const children = (node.children || [])
        .map(filterNode)
        .filter(Boolean);

      if (node.name.toLowerCase().includes(lower)) return node;
      if (acts.length > 0 || children.length > 0)
        return { ...node, activities: acts, children };
      return null;
    };

    return treeData.map(filterNode).filter(Boolean);
  }, [treeData, search]);

  /** ───────────────────────────────────────────
   *  📌 진도율 계산
   *  ─────────────────────────────────────────── */
  const { totalActual, totalPlan } = useMemo(() => {
    // 🔹 검색 없으면 계산 자체 안 함
    if (!search) {
      return { totalActual: 0, totalPlan: 0 };
    }

    let plan = 0;
    let actual = 0;

    const collect = (node) => {
      (node.activities || []).forEach((a) => {
        plan += Number(a.plan_labor) || 0;
        actual += Number(a.actual_labor) || 0;
      });

      (node.children || []).forEach(collect);
    };

    filteredTree.forEach(collect);

    return {
      totalPlan: plan,
      totalActual: actual,
    };
  }, [filteredTree, search]);


  const progressRate =
    totalPlan > 0 ? ((totalActual / totalPlan) * 100).toFixed(2) : 0;



  return (
    <div className="p-6 space-y-6">
      {/* ============================================
          📌 페이지 헤더
      ============================================= */}
      <PageHeader>공정 진도율</PageHeader>

      {/* 공정 요약 카드 */}
      {project?.summary && (
        <SummaryCard summary={project.summary} />
      )}

      {/* 실적 / 베이스라인 선택 */}
      <div className="flex flex-wrap items-center gap-4 mt-4">
        <label className="text-sm text-gray-500">실적 기준일</label>
        <select
          value={selectedActual || ""}
          onChange={(e) => setSelectedActual(e.target.value)}
          className="border rounded-lg px-3 py-1.5 text-sm"
        >
          {actualRuns.map((r) => (
            <option key={r.id} value={r.id}>
              {new Date(r.data_date).toLocaleDateString("ko-KR")}
            </option>
          ))}
        </select>

        <label className="text-sm text-gray-500">베이스라인</label>
        <select
          value={selectedBaseline || ""}
          onChange={(e) => setSelectedBaseline(e.target.value)}
          className="border rounded-lg px-3 py-1.5 text-sm"
        >
          {baselineRuns.map((r) => (
            <option key={r.id} value={r.id}>
              Rev.{r.revision || "-"}
            </option>
          ))}
        </select>
      </div>



      {/* ============================================
          📌 검색
      ============================================= */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="검색 (공정명/코드)"
          className="border rounded-lg px-3 py-1.5 text-sm w-64 focus:ring-1 focus:ring-blue-500"
        />
        {search && (
          <span className="text-sm text-gray-600">
            검색 진도율:
            <span className="ml-1 font-semibold text-blue-600">
              {progressRate}%
            </span>
          </span>
        )}
      </div>

      {/* ============================================
          📌 트리 테이블
      ============================================= */}
      <div className="mt-2">
        {loading ? (
          <div className="p-6 text-center text-gray-500">📊 데이터 로딩 중...</div>
        ) : (
          <TreeTable treeData={filteredTree} />
        )}
      </div>
    </div>
  );
};

export default Progress;
